import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/constants";
import { History, ShoppingCart } from "lucide-react";

export default function PaymentHistorySheet({ debt, onClose }) {
  const [rows, setRows] = useState(null);
  const [charges, setCharges] = useState([]);

  useEffect(() => {
    if (!debt) { setRows(null); setCharges([]); return; }
    api.get(`/debts/${debt.id}/payments`).then(r => setRows(r.data));
    if (debt.category === "credit_card") api.get(`/debts/${debt.id}/charges`).then(r => setCharges(r.data)).catch(() => setCharges([]));
  }, [debt]);

  const total = rows?.reduce((s, p) => s + p.payment, 0) || 0;
  const interest = rows?.reduce((s, p) => s + p.interest, 0) || 0;
  const principal = rows?.reduce((s, p) => s + p.principal, 0) || 0;
  const intPct = total ? Math.round((interest / total) * 100) : 0;

  return (
    <Sheet open={!!debt} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="sm:max-w-md w-full overflow-y-auto thin-scroll bg-card" data-testid="payment-history-sheet">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2"><History className="h-4 w-4 text-indigo-400" /> {debt?.name} · payment history</SheetTitle>
          <SheetDescription>Every logged payment, split between interest and principal.</SheetDescription>
        </SheetHeader>

        <div className="mt-5 p-4 rounded-xl border border-border bg-accent/30">
          <div className="flex justify-between text-xs text-muted-foreground"><span>Total paid</span><span className="font-mono font-semibold text-foreground" data-testid="history-total">{fmt(total)}</span></div>
          <div className="mt-3 h-3 rounded-full overflow-hidden flex bg-accent" title={`${intPct}% interest`}>
            <div className="h-full bg-rose-500" style={{ width: `${intPct}%` }} />
            <div className="h-full bg-teal-500" style={{ width: `${100 - intPct}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-[11px]">
            <span className="text-rose-500 font-semibold">Interest {fmt(interest)} · {intPct}%</span>
            <span className="text-teal-500 font-semibold">Principal {fmt(principal)} · {100 - intPct}%</span>
          </div>
        </div>

        {rows === null && <div className="mt-6 h-24 rounded-xl bg-accent animate-pulse" />}
        {rows?.length === 0 && <div className="mt-8 text-center text-sm text-muted-foreground" data-testid="history-empty">No payments logged yet. Pay from "This month's payments" to start your history.</div>}
        {rows?.length > 0 && (
          <ul className="mt-5 space-y-2" data-testid="history-list">
            {rows.map((p, i) => {
              const ip = p.payment ? (p.interest / p.payment) * 100 : 0;
              return (
                <li key={p.id ?? i} className="p-3 rounded-xl border border-border/60 bg-background/60" data-testid={`history-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">{fmtDate(p.paid_at)}</div>
                    <div className="font-mono font-semibold">{fmt(p.payment)}</div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full overflow-hidden flex bg-accent">
                    <div className="h-full bg-rose-500" style={{ width: `${ip}%` }} />
                    <div className="h-full bg-teal-500" style={{ width: `${100 - ip}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[11px] font-mono">
                    <span className="text-rose-500">{fmt(p.interest)} interest</span>
                    <span className="text-teal-500">{fmt(p.principal)} principal</span>
                    <span className="text-muted-foreground">→ {fmt(p.balance_after)}</span>
                  </div>
                  {p.paid_off && <div className="mt-1.5 text-[10px] uppercase tracking-wider font-bold text-emerald-500">Paid off</div>}
                </li>
              );
            })}
          </ul>
        )}
        {charges.length > 0 && (
          <div className="mt-6" data-testid="charges-section">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5"><ShoppingCart className="h-3 w-3 text-rose-400" /> Charges logged · {fmt(charges.reduce((s, c) => s + c.amount, 0))}</div>
            <ul className="mt-2 space-y-1.5">
              {charges.map(c => (
                <li key={c.id} className="p-2.5 rounded-lg border border-border/60 bg-background/60 flex items-center justify-between text-xs" data-testid={`charge-row-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{c.description || "Charge"}</div>
                    <div className="text-[10px] text-muted-foreground">{fmtDate(c.date)} · balance → {fmt(c.balance_after)}</div>
                  </div>
                  <div className="font-mono font-semibold text-rose-400">+{fmt(c.amount)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
